import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from '@/api/rpc/client';
import type {
  ConfirmDocumentUploadRequest,
  ConfirmDocumentUploadResponse,
  RemoveDocumentRequest,
  RemoveDocumentResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

interface UploadDocumentInput {
  agreementId: string;
  file: File;
}

interface UploadDocumentResult {
  documentId: string;
  name: string;
  storagePath: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
  status: string;
}

/**
 * Hook for uploading a document to a rebate agreement.
 * Handles the full presigned URL flow:
 * 1. Get presigned URL from backend
 * 2. Upload file directly to storage
 * 3. Confirm upload with backend
 */
export function useUploadAgreementDocument() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<UploadDocumentResult, Error, UploadDocumentInput>({
    mutationFn: async ({ agreementId, file }) => {
      // Step 1: Get presigned upload URL
      const { documentId, uploadUrl } =
        await rpcClient.rebates.agreementDocuments.getUploadUrl({
          agreementId,
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
        });

      // Step 2: Upload file directly to storage
      // Note: Azure Blob Storage requires x-ms-blob-type header for PUT uploads
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'x-ms-blob-type': 'BlockBlob',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(
          `Failed to upload file: ${uploadResponse.status} ${uploadResponse.statusText}`,
        );
      }

      // Step 3: Confirm upload with backend
      const confirmInput: ConfirmDocumentUploadRequest = {
        documentId,
        agreementId,
        fileSizeBytes: file.size,
      };

      const confirmResult: ConfirmDocumentUploadResponse =
        await rpcClient.rebates.agreementDocuments.confirmUpload(confirmInput);

      return {
        documentId: confirmResult.document.id,
        name: confirmResult.document.name,
        storagePath: confirmResult.document.storagePath,
        mimeType: confirmResult.document.mimeType,
        fileSizeBytes: confirmResult.document.fileSizeBytes,
        status: confirmResult.document.status,
      };
    },
    onSuccess: (data, variables) => {
      showToast({
        title: 'Document Uploaded',
        message: `"${data.name}" has been added to the agreement`,
        color: 'success',
        icon: 'check',
      });

      // Invalidate rebate agreement query to refresh documents
      void queryClient.invalidateQueries({
        queryKey: ['rebates/agreements', variables.agreementId],
      });
      // Also invalidate the specific endpoint query key
      void queryClient.invalidateQueries({
        queryKey: [`rebates/agreements/${variables.agreementId}`],
      });
    },
    onError: (error) => {
      showToast({
        title: 'Upload Failed',
        message: error.message || 'Failed to upload document',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/**
 * Hook for removing a document from a rebate agreement.
 * Removes the association and deletes the document if it has no other associations.
 */
export function useRemoveAgreementDocument() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<RemoveDocumentResponse, Error, RemoveDocumentRequest>({
    mutationFn: async (input) => {
      return await rpcClient.rebates.agreementDocuments.remove(input);
    },
    onSuccess: (_data, variables) => {
      showToast({
        title: 'Document Removed',
        message: 'Document has been removed from the agreement',
        color: 'success',
        icon: 'check',
      });

      // Invalidate rebate agreement query to refresh documents
      void queryClient.invalidateQueries({
        queryKey: ['rebates/agreements', variables.agreementId],
      });
      // Also invalidate the specific endpoint query key
      void queryClient.invalidateQueries({
        queryKey: [`rebates/agreements/${variables.agreementId}`],
      });
    },
    onError: (error) => {
      showToast({
        title: 'Removal Failed',
        message: error.message || 'Failed to remove document',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
